import React, { useState, useEffect, useRef, useContext } from 'react';
import styled from 'styled-components';
import { UserVote } from './VoteStorage';
import VoteDetailModal from './VoteDetailModal';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import UserContext from '../../Contexts/User';
import Swal from 'sweetalert2';
import WaitForResponse from '../Profile/CardTrade/WaitForResponse';
import { scrollToCenter } from 'common/utils/helper';
import ShareModal from 'Components/shareModal';

interface VotesListProps {
  onEditVote: (vote: UserVote) => void;
  onDeleteVote: (voteId: string) => void;
  draftFilter: boolean;
}

// Styled Components
const Container = styled.div`
  padding: 1rem;
  min-height: 70vh;
  // max-height: auto;
  overflow-y: auto;
`;

const VoteState = styled.div`
  position:absolute;
  bottom:0;
  left:0;  
  width:100%;  
  border-radius:0px 0px 10px 10px;   
  font-size: 0.875rem;
  font-weight: 500; 
  height: 30px;
  background:#3030309c;
`;

const StatBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  height: 100%;
  color: #fff;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.1);
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e5e7eb;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
`;

const VoteCount = styled.span`
  background: #3b82f6;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.875rem;
  font-weight: 500;
`;

const SearchContainer = styled.div`
  margin-bottom: 2rem;
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 250px;
  
  @media (max-width: 768px) {
    width: 100%;
    min-width: unset;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.875rem;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
  
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.15);
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.1);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.7);
  font-size: 1rem;
  pointer-events: none;
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const FilterSelect = styled.select`
  padding: 0.75rem 1rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 0.875rem;
  backdrop-filter: blur(10px);
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;
  
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.4);
    background: rgba(255, 255, 255, 0.15);
  }
  
  option {
    background: #1f2937;
    color: white;
  }
`;

const FilterLabel = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
`;

const VoteCard = styled.div<{ hasMedia?: boolean }>`
  position: relative;
  background: ${props => props.hasMedia ? 'transparent' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  border-radius: 1rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  overflow: hidden;
  min-height: 280px;
  cursor: pointer;

  &:hover {
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    min-height: 240px;
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    min-height: 200px;
  }
`;

const MediaBackground = styled.div<{ fileType?: string; hasFile?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.hasFile
    ? props.fileType?.startsWith('image/')
      ? 'linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.6))'
      : 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.7))'
    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  opacity: ${props => props.hasFile ? 0.9 : 1};
  z-index: 0;

  @media (max-width: 768px) {
    font-size: 3rem;
  }

  @media (max-width: 480px) {
    font-size: 2.5rem;
  }
`;

const CardContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 1.5rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (max-width: 768px) {
    padding: 1.25rem;
  }

  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

const VoteHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const VoteTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0;
  flex: 1;
  margin-right: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  line-height: 1.3;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }

  @media (max-width: 480px) {
    font-size: 1.125rem;
  }
`;

const ThreeDotsButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  position: relative;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
  }
`;

const DotsIcon = styled.div`
  color: white;
  font-size: 1.2rem;
  font-weight: bold;
  line-height: 1;
`;

const DropdownMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  min-width: 120px;
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
  overflow: hidden;
`;

const DropdownItem = styled.button<{ variant?: 'edit' | 'delete' }>`
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.variant === 'delete' ? '#ef4444' : '#374151'};
  transition: background-color 0.2s ease;

  &:hover {
    background: ${props => props.variant === 'delete' ? '#fef2f2' : '#f3f4f6'};
  }

  &:first-child {
    border-bottom: 1px solid #e5e7eb;
  }
`;

const VoteOptions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const OptionCard = styled.div<{ isSelected?: boolean }>`
  padding: 0.75rem; 
  // border: 2px solid ${props => props.isSelected ? '#3b82f6' : '#e5e7eb'};
  border-radius: 0.375rem;
  // background: ${props => props.isSelected ? '#eff6ff' : '#f9fafb'};
  text-align: center;  
`;

const OptionIcon = styled.div`
  font-size: 1.5rem;
  margin-bottom: 0.25rem;
`;

const OptionText = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
`;

const Timestamp = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
  text-align: right;
`;

const VotesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 1.25rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 0 0 1rem 0;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color:rgb(255, 255, 255);
  font-family: 'Poppins', sans-serif;
`;

const EmptyIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;  
`;
const VotesList: React.FC<VotesListProps> = ({ onEditVote, onDeleteVote, draftFilter }) => {
  // Get user context for Firebase functions
  const { user, userInfo } = useContext(UserContext);

  // Firebase functions
  const getAllDailyMatchupsByUserId = httpsCallable(functions, 'getAllDailyMatchupsByUserId');
  const deleteDailyMatchupById = httpsCallable(functions, 'deleteDailyMatchupById');

  const [votes, setVotes] = useState<UserVote[]>([]);
  const [Allvotes, setAllVotes] = useState<UserVote[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredVotes, setFilteredVotes] = useState<UserVote[]>([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [selectedVote, setSelectedVote] = useState<UserVote | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [shareModalShow, setShareModalShow] = useState(false);
  const [shareVoteUrl, setShareVoteUrl] = useState('');

  useEffect(() => {
    loadVotes();
  }, []);

  useEffect(() => {
    let filtered = votes;

    // Apply search filter
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(vote =>
        vote.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vote.option1.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vote.option2.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(vote => {
        const voteStatus = vote.status || 'pending';
        return voteStatus === statusFilter;
      });
    }
    if (draftFilter != undefined) {
      console.log(draftFilter, "draftFilter")
      filtered = filtered.filter(vote => {
        const votedraft = vote?.isPublished;
        return votedraft != draftFilter;
      });
    }

    setFilteredVotes(filtered);
  }, [searchTerm, statusFilter, votes, draftFilter]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedOutside = Object.values(dropdownRefs.current).every(ref =>
        !ref || !ref.contains(event.target as Node)
      );

      if (clickedOutside) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadVotes = async () => {
    if (!user?.uid) {
      setError('User not authenticated');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Call Firebase function to get all daily matchups for the user
      const result = await getAllDailyMatchupsByUserId({ userId: user.uid });
      console.log('Daily matchups loaded:', result.data);

      // Transform Firebase data to UserVote format
      const responseData = result.data as any;

      // Handle the response structure: { result: { success: true, matchups: [...], count: number } }
      const firebaseVotes = responseData?.result?.matchups || responseData?.matchups || [];
      if (firebaseVotes && firebaseVotes.length > 0) {
        const transformedVotes: UserVote[] = firebaseVotes.map((vote: any) => ({
          id: vote.id || Date.now().toString(),
          title: vote.headline || vote.title || '', // Use headline from Firebase
          file: null, // Firebase stores URLs, not File objects
          fileName: vote.fileName || '',
          fileType: vote.media_type || 'image', // Default to image
          imageUrl: vote.imageUrl?.trim() || '',
          videoUrl: vote.videoUrl?.trim() || '',
          category: vote.category || '', // Add category from Firebase
          option1: vote.thumbs_meaning?.up || vote.option1 || '',
          option2: vote.thumbs_meaning?.down || vote.option2 || '',
          option1Icon: vote.option1Icon || '', // Default icons
          option2Icon: vote.option2Icon || '', // Default icons
          createdAt: vote.createdAt?._seconds ? vote.createdAt._seconds * 1000 : (vote.createdAt || Date.now()),
          updatedAt: vote.updatedAt?._seconds ? vote.updatedAt._seconds * 1000 : (vote.updatedAt || Date.now()),
          status: vote.status || 'pending',
          votes: vote.votes || {},
          isPublished: vote?.isPublished == undefined ? true : vote?.isPublished,
          likeCount: vote?.likeCount || 0,
        }));

        setVotes(transformedVotes);
        console.log('Loaded votes from Firebase:', transformedVotes);
      } else {
        setVotes([]);
        console.log('No votes found in Firebase');
      }
    } catch (error) {
      console.error('Error loading votes from Firebase:', error);
      setError('Failed to load votes. Please try again.');
      setVotes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteVote = async (voteId: string) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You want to delete this vote? This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await deleteDailyMatchupById({ id: voteId });
        console.log('Vote deleted successfully:', voteId);

        // Show success message
        await Swal.fire({
          title: 'Deleted!',
          text: 'Your vote has been deleted successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });

        loadVotes(); // Reload the votes list
        onDeleteVote(voteId);
      } catch (error) {
        console.error('Error deleting vote:', error);

        // Show error message
        await Swal.fire({
          title: 'Error!',
          text: 'Failed to delete vote. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } finally {
        setLoading(false);
      }
    }
    setOpenDropdown(null);
  };

  // const handleEditVote = (vote: UserVote) => {
  //   onEditVote(vote);
  //   setOpenDropdown(null);
  // };

  const toggleDropdown = (voteId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenDropdown(openDropdown === voteId ? null : voteId);
  };

  const handleCardClick = (vote: UserVote) => {
    if (scrollToCenter) {
      scrollToCenter();
    }
    setSelectedVote(vote);
    setIsModalOpen(true);
    setOpenDropdown(null); // Close any open dropdown
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedVote(null);
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (fileType?: string) => {
    if (!fileType) return '📄';
    if (fileType.startsWith('image/')) return '🖼️';
    if (fileType.startsWith('video/')) return '🎥';
    return '📄';
  };

  const formatCount = (n: number): string => {
    if (!Number.isFinite(n)) return '0';
    if (n >= 1000) {
      const s = (n / 1000).toFixed(1).replace(/\.0$/, '');
      return `${s}k`;
    }
    return `${n}`;
  };

  const handleShareClick = (vote: UserVote, event: React.MouseEvent) => {
    event.stopPropagation();
    const voteUrl = `${window.location.origin}?id=${vote.id}`;
    setShareVoteUrl(voteUrl);
    setShareModalShow(true);
  };

  if (loading === false && votes.length === 0) {
    return (
      <Container>
        <EmptyState>
          <EmptyIcon>🗳️</EmptyIcon>
          <h3>No votes created yet</h3>
          <p>Create your first vote to get started!</p>
        </EmptyState>
      </Container>
    );
  }
  return (
    <Container>
      {/* <Header>
        <Title>My Votes</Title>
        <VoteCount>{votes.length} votes</VoteCount>
      </Header> */}

      <SearchContainer>
        <SearchInputWrapper>
          <SearchIcon>🔍</SearchIcon>
          <SearchInput
            type="text"
            placeholder="Search votes by title or options..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInputWrapper>

        <FilterContainer>
          <FilterLabel>Status:</FilterLabel>
          <FilterSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="active">Approved</option>
          </FilterSelect>
        </FilterContainer>
      </SearchContainer>

      {loading ? (
        <WaitForResponse />
      ) : error ? (
        <EmptyState>
          <EmptyIcon>❌</EmptyIcon>
          <h3>Error loading votes</h3>
          <p>{error}</p>
          <button
            onClick={loadVotes}
            style={{
              marginTop: '10px',
              padding: '10px 20px',
              cursor: 'pointer',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            Retry
          </button>
        </EmptyState>
      ) : loading == false && filteredVotes.length === 0 ? (
        <EmptyState>
          <EmptyIcon>🔍</EmptyIcon>
          <h3>No votes found</h3>
          <p>Try adjusting your filter terms</p>
        </EmptyState>
      ) : (
        <VotesGrid>
          {filteredVotes.map((vote) => {
            const countsSource: any = (vote as any)?.votes ?? (vote as any);
            const likeCount: number = (vote as any)?.likes ?? (vote as any)?.likeCount ?? 0;
            const upCount: number = typeof countsSource?.up === 'number' ? countsSource.up : ((vote as any)?.up ?? 0);
            const downCount: number = typeof countsSource?.down === 'number' ? countsSource.down : ((vote as any)?.down ?? 0);
            const TotalCount: number = (countsSource?.up || 0) + (countsSource?.down || 0);
            return (
              <VoteCard key={vote.id} hasMedia={!!vote.imageUrl} onClick={() => handleCardClick(vote)}>
                {/* Background Media or Gradient */}
                <MediaBackground fileType={vote.fileType} hasFile={!!vote.imageUrl || !!vote.videoUrl}>
                  {vote?.imageUrl && vote.fileType && vote.fileType.startsWith('image') && (
                    <img
                      src={vote.imageUrl}
                      alt="Vote background"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 1
                      }}
                    />
                  )}
                  {vote?.videoUrl && (

                    <video
                      src={vote.videoUrl}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        zIndex: 1
                      }}
                      controls={false}
                      autoPlay={false}
                      muted
                      loop
                    />
                  )}
                </MediaBackground>

                <CardContent>
                  <VoteHeader>
                    <div>
                      <h3 style={{ color: 'white', margin: 0 }}>{vote.title}</h3>
                      <Timestamp>{/* {formatDate(vote.createdAt)} */}</Timestamp>
                    </div>
                    <div style={{ position: 'relative' }} ref={el => dropdownRefs.current[vote.id] = el}>
                      <ThreeDotsButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenDropdown(openDropdown === vote.id ? null : vote.id);
                        }}
                      >
                        <DotsIcon>⋯</DotsIcon>
                      </ThreeDotsButton>
                      <DropdownMenu isOpen={openDropdown === vote.id}>
                        {!vote.isPublished && <DropdownItem
                          variant="edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditVote(vote);
                            setOpenDropdown(null);
                          }}
                        >
                          Edit
                        </DropdownItem>}
                        <DropdownItem
                          variant="delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteVote(vote.id);
                            setOpenDropdown(null);
                          }}
                        >
                          Delete
                        </DropdownItem>
                      </DropdownMenu>
                    </div>
                  </VoteHeader>

                  <VoteOptions>
                    <OptionCard>
                      <OptionIcon>{vote.option1Icon}</OptionIcon>
                      <OptionText>{vote.option1}</OptionText>
                    </OptionCard>
                    <OptionCard>
                      <OptionIcon>{vote.option2Icon}</OptionIcon>
                      <OptionText>{vote.option2}</OptionText>
                    </OptionCard>
                  </VoteOptions>
                  <VoteState>
                    <StatBar>
                      <StatItem>
                        <span aria-label="like" role="img">❤️</span>
                        <span>{formatCount(likeCount)}</span>
                      </StatItem>
                      <StatItem>
                        <span aria-label="thumbs up" role="img">👍</span>
                        <span>{formatCount(upCount)}</span>
                      </StatItem>
                      <StatItem>
                        <span aria-label="thumbs down" role="img">👎</span>
                        <span>{formatCount(downCount)}</span>
                      </StatItem>
                      <StatItem>
                        <span aria-label="total votes" role="img">👥</span>
                        <span>{formatCount(TotalCount)}</span>
                      </StatItem>
                      <StatItem onClick={(e) => handleShareClick(vote, e)}>
                        <span aria-label="share" role="img">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            style={{ display: 'inline-block', verticalAlign: 'middle' }}
                          >
                            <path d="M21 12l-7-7v4C7 10 4 15 3 20c2.5-3.5 6-5.1 11-5.1V19l7-7z" />
                          </svg>
                        </span>
                        <span>Share</span>
                      </StatItem>
                    </StatBar>
                  </VoteState>

                </CardContent>
              </VoteCard>
            );
          })}
        </VotesGrid>
      )}

      <VoteDetailModal
        vote={selectedVote}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />

      <ShareModal
        shareModalShow={shareModalShow}
        setShareModalShow={setShareModalShow}
        url={shareVoteUrl}
        shareText="Give a vote on these votes and win unique cards!"
        hedingText="Share your vote → earn more. Friends who join from your link help you mine faster and if they buy in-app, you get a cut."
        fullUrl={shareVoteUrl}
      />
    </Container>
  );
};

export default VotesList;